import React, { useEffect, useState } from 'react';
import { TrashIcon, EnvelopeOpenIcon, EnvelopeIcon, PaperClipIcon } from '@heroicons/react/24/outline';
import API from '../api';

const BASE = process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL.replace('/api', '') : 'http://localhost:5000';

function formatDate(str) {
  return new Date(str).toLocaleString('en-MY', { dateStyle: 'medium', timeStyle: 'short' });
}

export default function AdminMessages() {
  const [messages, setMessages] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => API.get('/contact').then(r => setMessages(r.data)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const handleOpen = async (msg) => {
    setSelected(msg);
    if (!msg.is_read) {
      await API.put(`/contact/${msg.id}/read`).catch(() => {});
      setMessages(ms => ms.map(m => m.id === msg.id ? { ...m, is_read: 1 } : m));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this message?')) return;
    await API.delete(`/contact/${id}`);
    if (selected?.id === id) setSelected(null);
    load();
  };

  const unread = messages.filter(m => !m.is_read).length;

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-display font-bold text-primary-dark">
          Messages
          {unread > 0 && (
            <span className="ml-3 text-sm bg-accent text-white px-2.5 py-0.5 font-normal">{unread} unread</span>
          )}
        </h1>
        <p className="text-sm text-gray-500 mt-1">Contact form submissions from visitors</p>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="w-7 h-7 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 min-h-[500px]">
          {/* Message list */}
          <div className="lg:col-span-2 bg-white border border-gray-100 shadow-sm overflow-hidden flex flex-col">
            {messages.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center text-gray-400 py-20 px-4 text-center">
                <EnvelopeOpenIcon className="w-12 h-12 mb-3 opacity-30" />
                <p>No messages yet.</p>
              </div>
            ) : (
              <div className="overflow-y-auto divide-y divide-gray-50">
                {messages.map(msg => (
                  <button
                    key={msg.id}
                    onClick={() => handleOpen(msg)}
                    className={`w-full text-left px-4 py-4 transition-colors hover:bg-gray-50 ${selected?.id === msg.id ? 'bg-primary/5 border-l-2 border-primary' : 'border-l-2 border-transparent'}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        {msg.is_read
                          ? <EnvelopeOpenIcon className="w-4 h-4 text-gray-300 shrink-0" />
                          : <EnvelopeIcon className="w-4 h-4 text-accent shrink-0" />}
                        <span className={`text-sm truncate ${!msg.is_read ? 'font-semibold text-gray-800' : 'text-gray-600'}`}>
                          {msg.name}
                        </span>
                      </div>
                      <span className="text-xs text-gray-400 shrink-0">{formatDate(msg.created_at)}</span>
                    </div>
                    <p className="text-xs text-gray-400 mt-1 truncate ml-6">{msg.email}</p>
                    <p className="text-xs text-gray-500 mt-1 ml-6 line-clamp-2">{msg.message}</p>
                    {msg.attachments?.length > 0 && (
                      <p className="flex items-center gap-1 text-xs text-gray-400 mt-1 ml-6">
                        <PaperClipIcon className="w-3 h-3" /> {msg.attachments.length}
                      </p>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Message detail */}
          <div className="lg:col-span-3 bg-white border border-gray-100 shadow-sm">
            {!selected ? (
              <div className="h-full flex flex-col items-center justify-center text-gray-400 py-20">
                <EnvelopeOpenIcon className="w-14 h-14 mb-3 opacity-20" />
                <p className="text-sm">Select a message to read</p>
              </div>
            ) : (
              <div className="p-6 h-full flex flex-col">
                <div className="flex items-start justify-between gap-4 mb-6 pb-6 border-b border-gray-100">
                  <div>
                    <h2 className="font-semibold text-gray-800 text-lg">{selected.name}</h2>
                    <div className="text-sm text-gray-500 mt-0.5 space-y-0.5">
                      <div><span className="font-medium text-gray-600">Email:</span> <a href={`mailto:${selected.email}`} className="text-primary hover:text-accent transition-colors">{selected.email}</a></div>
                      {selected.phone && <div><span className="font-medium text-gray-600">Phone:</span> {selected.phone}</div>}
                      {selected.company && <div><span className="font-medium text-gray-600">Company:</span> {selected.company}</div>}
                      {selected.peza_type && <div><span className="font-medium text-gray-600">PEZA Enterprise Type:</span> {selected.peza_type}</div>}
                      <div><span className="font-medium text-gray-600">Received:</span> {formatDate(selected.created_at)}</div>
                    </div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <a href={`mailto:${selected.email}`}
                      className="btn-primary text-xs py-2 px-3">Reply</a>
                    <button onClick={() => handleDelete(selected.id)}
                      className="p-2 text-gray-400 hover:text-red-500 border border-gray-200 hover:border-red-300 transition-colors">
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto">
                  <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Message</h3>
                  <p className="text-gray-700 text-sm leading-relaxed whitespace-pre-wrap">{selected.message}</p>

                  {selected.attachments?.length > 0 && (
                    <div className="mt-6">
                      <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
                        Attachments ({selected.attachments.length})
                      </h3>
                      <div className="flex flex-wrap gap-3">
                        {selected.attachments.map((url, i) => (
                          <a key={i} href={`${BASE}${url}`} target="_blank" rel="noopener noreferrer"
                            className="w-24 h-24 border border-gray-200 overflow-hidden hover:opacity-80 transition-opacity">
                            <img src={`${BASE}${url}`} alt={`Attachment ${i + 1}`} className="w-full h-full object-cover" />
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
